import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../app/http-common';
import { useHasFeature } from '../../hooks/useFrontegg';
import HelpModal from './components/HelpModal';
import './SocialPosts.css';

const STATUS_LABELS = {
  draft: 'Draft',
  ready: 'Ready',
  approved: 'Approved',
  failed: 'Failed',
};

function SocialPostsList() {
  const navigate = useNavigate();
  const { isEntitled, isLoading: featureLoading } = useHasFeature('social-posts-creation');
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const data = await api.getSocialPosts(params);
      setPosts(data.posts || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    if (isEntitled) fetchPosts();
  }, [isEntitled, fetchPosts]);

  if (featureLoading) return <div className="social-posts-loading">Loading...</div>;
  if (!isEntitled) return <div className="social-posts-no-access">Social Posts feature is not available for your account.</div>;

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="social-posts-list">
      {/* Page Hero */}
      <div className="social-posts-page-hero">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h2>📱 Social Posts</h2>
          <button
            onClick={() => setHelpOpen(true)}
            style={{
              width: 24, height: 24, borderRadius: '50%',
              border: '1px solid #555', background: 'transparent',
              color: '#999', cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            title="How to use Social Posts"
          >
            ?
          </button>
        </div>
        <p className="social-posts-page-hero-subtitle">
          Transform your news briefs into eye-catching social media content. Generate slides, edit captions, and export ready-to-publish posts.
        </p>
      </div>

      {/* Workflow Guide */}
      <div className="social-posts-workflow">
        <div className="social-posts-workflow-step">
          <span className="social-posts-workflow-step-num">1</span>
          <span className="social-posts-workflow-step-text">
            <strong>Generate</strong>Click "Create Social Post" on any story card to generate slides automatically.
          </span>
        </div>
        <div className="social-posts-workflow-step">
          <span className="social-posts-workflow-step-num">2</span>
          <span className="social-posts-workflow-step-text">
            <strong>Edit</strong>Customize captions, hashtags, and slide headlines to match your voice.
          </span>
        </div>
        <div className="social-posts-workflow-step">
          <span className="social-posts-workflow-step-num">3</span>
          <span className="social-posts-workflow-step-text">
            <strong>Render</strong>Click "Render Slides" to produce final images from your content.
          </span>
        </div>
        <div className="social-posts-workflow-step">
          <span className="social-posts-workflow-step-num">4</span>
          <span className="social-posts-workflow-step-text">
            <strong>Export</strong>Download a ZIP with all slides and captions, ready to publish.
          </span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="social-posts-toolbar">
        <div className="social-posts-filters">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        {!loading && total > 0 && (
          <span className="social-posts-count">{total} post{total !== 1 ? 's' : ''}</span>
        )}
      </div>

      {error && <div className="social-posts-error">{error}</div>}

      {loading ? (
        <div className="social-posts-loading">Loading posts...</div>
      ) : posts.length === 0 ? (
        <div className="social-posts-empty">
          <div className="social-posts-empty-icon">📋</div>
          <h3 className="social-posts-empty-title">No social posts yet</h3>
          <p className="social-posts-empty-text">
            To create your first social post, go to the news feed, find a story you like, 
            and click the <strong>"Create Social Post"</strong> button on the story card.
          </p>
        </div>
      ) : (
        <>
          <div className="social-posts-table-wrapper">
            <table className="social-posts-table">
              <thead>
                <tr>
                  <th>Story</th>
                  <th>Platform</th>
                  <th>Status</th>
                  <th>Export</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post._id}>
                    <td className="social-posts-story-cell">
                      {post.story?.title || post.story?.subject || 'Untitled'}
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>{post.platform}</td>
                    <td>
                      <span className={`social-posts-status social-posts-status-${post.status}`}>
                        {STATUS_LABELS[post.status] || post.status}
                      </span>
                    </td>
                    <td>
                      <span className={`social-posts-export-status social-posts-export-${post.exportStatus}`}>
                        {post.exportStatus || 'none'}
                      </span>
                    </td>
                    <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="social-posts-btn social-posts-btn-view social-posts-btn-small"
                        onClick={() => navigate(`/social-posts/${post._id}`)}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 20 && (
            <div className="social-posts-pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} context="list" />
    </div>
  );
}

export default SocialPostsList;
